import React, { Component } from 'react';
import { bool, func, number, object, string, array } from 'prop-types';
import Dropdown from '../Dropdown/Dropdown';
import classnames from 'classnames';

import './Pagination.scss';

class Pagination extends Component {
  static propTypes = {
    className: string,
    containerClassName: string,
    pageAction: func,
    // step is the limit
    step: number,
    pageShowRange: number,
    offset: number,
    totalCount: number,
    scrollEl: object,
    scrollTopPageChange: bool,
    scrollTopOffset: number,
    scrollTarget: object,
    onStepChange: func,
    allowChangingLimit: bool,
    dropdownItems: array,
    prependText: string,
    middleText: string,
    appendText: string
  };

  static defaultProps = {
    pageShowRange: 2,
    scrollTopOffset: 0,
    prependText: '',
    middleText: 'of',
    appendText: '',
    allowChangingLimit: false,
    dropdownItems: [10, 25, 50, 100, 250, 500]
  };

  constructor(props) {
    super(props);
    this.scrollRef = React.createRef();
    this.setPage = this.setPage.bind(this);
    this.navPrevPage = this.navPrevPage.bind(this);
    this.navNextPage = this.navNextPage.bind(this);
    this.navFirstPage = this.navFirstPage.bind(this);
    this.navLastPage = this.navLastPage.bind(this);
    this.getPaginationKeys = this.getPaginationKeys.bind(this);
  }

  setPage(val) {
    const value = Number.isInteger(val) ? val : parseInt(val.currentTarget.dataset.page);
    const { pageAction, step, totalCount, scrollEl, scrollTopPageChange, scrollTarget, scrollTopOffset } = this.props;
    const newAfterValue = step * value - step;
    const isValueInRange = value > 0 && value <= Math.ceil(totalCount / step);
    if (isValueInRange) pageAction(step, newAfterValue);
    if (scrollEl && scrollTopPageChange)
      scrollEl.current.scrollTop = scrollTarget ? scrollTarget.current.offsetTop + scrollTopOffset : 0;
  }

  navNextPage() {
    const { totalCount, step, offset } = this.props;
    const next = Math.ceil(offset / step) + 2 <= Math.ceil(totalCount / step) ? Math.ceil(offset / step) + 2 : null;
    if (!next) return;
    this.setPage(next);
  }

  navPrevPage() {
    const { step, offset } = this.props;
    const prev = Math.ceil(offset / step) > 0 ? Math.ceil(offset / step) : null;
    if (!prev) return;
    this.setPage(prev);
  }

  navFirstPage(disabled) {
    if (disabled || Math.ceil(this.props.offset, this.props.step) + 1 <= 1) return;
    this.setPage(1);
  }

  //If totalCount changes (search occurs), navigate user to first page of results
  componentDidUpdate(prevProps) {
    return prevProps.totalCount !== this.props.totalCount ? this.navFirstPage(false) : null;
  }

  navLastPage(disabled) {
    const { totalCount, step, offset } = this.props;
    if (disabled || Math.ceil(offset / step) + 1 >= Math.ceil(totalCount / step)) return;
    this.setPage(Math.ceil(totalCount / step));
  }

  getPaginationKeys(page, start, end) {
    let pageArray = [];
    for (let i = start; i <= end; i++) {
      if (page === i) {
        pageArray.push(
          <a key={i} className="pagination-nav-button pxl-btn pxl-btn-secondary pxl-btn-xs">
            {i}
          </a>
        );
      } else {
        pageArray.push(
          <a data-page={i} key={i} onClick={this.setPage} className="pagination-nav-page">
            {i}
          </a>
        );
      }
    }
    return pageArray;
  }

  render() {
    const {
      className,
      step,
      offset,
      totalCount,
      containerClassName,
      onStepChange,
      pageShowRange,
      allowChangingLimit,
      dropdownItems,
      prependText,
      middleText,
      appendText
    } = this.props;

    if (!totalCount) return null;

    const page = Math.ceil(offset / step) + 1;
    const isLastPage = page * step >= totalCount;
    const lowerLimit = offset + 1;
    const upperLimit = isLastPage ? totalCount : page * step;
    // Last possible page for navigation
    const lastPage = Math.ceil(totalCount / step);
    // Start page shown on navigation
    let startPage = 1;
    // If the left most displayed page is below page 1, set startPage to 1
    if (page - pageShowRange - 1 < 0) {
      startPage = 1;
      // If the last possible page is within the view,
      // set startPage to 2 times the specified pageShowRange
    } else if (lastPage < page + pageShowRange) {
      startPage = lastPage - 2 * pageShowRange < 1 ? 1 : lastPage - 2 * pageShowRange;
      // Otherwise, set startPage to the current page - the specified pageShowRange
    } else {
      startPage = page - pageShowRange;
    }

    // Finds the last page number to be shown
    let endPage = lastPage;
    // If the lastPage is within the view, set the endPage to the lastPage
    if (lastPage < page + pageShowRange) {
      endPage = lastPage;
      // If the page starts at page 1, then set the endpage to 2 times pageSHowRange +1
    } else if (page - pageShowRange - 1 < 0) {
      endPage = 2 * pageShowRange + 1 > lastPage ? lastPage : 2 * pageShowRange + 1;
      // Otherwise, set endPage to the current page + the specified pageShowRange
    } else {
      endPage = page + pageShowRange;
    }

    // Conditions for disabling and enabling arrow navigation based on position
    const showFirstPageArrow = startPage > 1;
    const showPrevArrow = page > 1;
    const showLastPageArrow = endPage < lastPage;
    const showNextArrow = page < lastPage;
    let pageArray = this.getPaginationKeys(page, startPage, endPage);

    let dropdownItemAfter,
      dropdownItemsLimited = dropdownItems.filter((el, idx) => {
        if (el < totalCount) {
          dropdownItemAfter = dropdownItems[idx + 1];
          return true;
        } else {
          return false;
        }
      });
    if (dropdownItemAfter) dropdownItemsLimited.push(dropdownItemAfter);

    return (
      <div ref={this.scrollRef} className={classnames('pagination__container', containerClassName)}>
        {allowChangingLimit && (
          <div className="pagination__step">
            Show
            {onStepChange ? (
              <Dropdown
                className="pagination__step-dropdown-container"
                dropdownClass="pagination__step-dropdown"
                itemOnClick={onStepChange}
                text={step}
                dropUp={true}
                dropdownItems={dropdownItemsLimited}
                disabled={false}
                rightBound={false}
                type="button"
                offset={offset}
              />
            ) : (
              ' ' + step + ' '
            )}
            per page
          </div>
        )}
        <div className={`pagination ${className}`}>
          <span
            className={classnames('pagination-arrow', { hide_arrow: !showFirstPageArrow })}
            onClick={this.navFirstPage.bind(this, !showFirstPageArrow)}
          >
            <i className="double_icon_left icon-left-open"></i>
            <i className="icon-left-open"></i>
          </span>
          <i
            onClick={this.navPrevPage}
            className={classnames('pagination-arrow icon-left-open', { hide_arrow: !showPrevArrow })}
          ></i>
          {pageArray}
          <i
            onClick={this.navNextPage}
            className={classnames('pagination-arrow icon-right-open', { hide_arrow: !showNextArrow })}
          ></i>
          <span
            className={classnames('pagination-arrow', { hide_arrow: !showLastPageArrow })}
            onClick={this.navLastPage.bind(this, !showLastPageArrow)}
          >
            <i className="double_icon_right icon-right-open"></i>
            <i className="icon-right-open"></i>
          </span>
        </div>
        <div>
          {upperLimit >= 1 && (
            <div className="pagination__counts">{`${prependText} ${lowerLimit}-${upperLimit} ${middleText} ${totalCount} ${appendText}`}</div>
          )}
        </div>
      </div>
    );
  }
}

export default Pagination;
